import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useOtpEntries, { decodeQrFromImageFile, parseAnyOtpQr } from '../hooks/useOtpEntries';
import { Input, PillButton, IconCircle, EmptyState, Modal, SegmentedControl, Toast } from '../ui';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as QrIcon } from '../assets/icons/qr-payment.svg';
import './TwoFactor.css';

// Ámbar los últimos 5s de la ventana de 30s: mismo umbral que llevaba OTPManager.js.
const AMBER_THRESHOLD = 5;

function initialsFor(entry) {
  const source = entry.issuer || entry.account || '?';
  return source.trim().slice(0, 2).toUpperCase();
}

function CountdownRing({ timeLeft, amber }) {
  const pct = timeLeft / 30;
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" className={`sgc-2fa__ring ${amber ? 'sgc-2fa__ring--amber' : ''}`}>
      <circle cx="18" cy="18" r={radius} className="sgc-2fa__ring-track" />
      <circle
        cx="18" cy="18" r={radius}
        className="sgc-2fa__ring-fill"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - pct)}
      />
      <text x="18" y="22" textAnchor="middle" className="sgc-2fa__ring-text">{timeLeft}</text>
    </svg>
  );
}

/** Modal de alta: pegar otpauth://, secreto manual o subir imagen QR. */
function AddEntryModal({ open, onClose, onAdd }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('manual');
  const [issuer, setIssuer] = useState('');
  const [account, setAccount] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const reset = () => { setIssuer(''); setAccount(''); setSecret(''); setError(''); setMode('manual'); };
  const close = () => { reset(); onClose(); };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const raw = secret.trim();
    if (!raw) { setError(t('otp.secretRequired')); return; }

    if (raw.startsWith('otpauth://')) {
      const [parsed] = parseAnyOtpQr(raw);
      if (!parsed) { setError(t('otp.invalidQR')); return; }
      onAdd(parsed);
      close();
      return;
    }

    onAdd({
      type: 'totp',
      issuer,
      account,
      secret: raw.replace(/\s/g, '').toUpperCase(),
      digits: 6,
      period: 30,
      algorithm: 'SHA1'
    });
    close();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const qrText = await decodeQrFromImageFile(file);
      const parsedList = parseAnyOtpQr(qrText);
      if (parsedList.length === 0) { setError(t('otp.invalidQR')); return; }
      parsedList.forEach((p) => onAdd(p));
      close();
    } catch {
      setError(t('otp.noQRFound'));
    }
  };

  return (
    <Modal open={open} onClose={close} title={t('otp.addEntry')}>
      <SegmentedControl
        className="sgc-2fa__add-modes"
        value={mode}
        onChange={setMode}
        options={[
          { value: 'manual', label: t('otp.manualEntry') },
          { value: 'image', label: t('otp.uploadQRImage') }
        ]}
      />

      {mode === 'manual' ? (
        <form className="sgc-2fa__add-form" onSubmit={handleManualSubmit}>
          <Input icon={ShieldIcon} label={t('otp.issuer')} value={issuer} onChange={(e) => setIssuer(e.target.value)} />
          <Input label={t('otp.account')} value={account} onChange={(e) => setAccount(e.target.value)} />
          <Input
            icon={KeyIcon}
            label={t('otp.secret')}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            error={error}
            placeholder={t('otp.secretPlaceholder')}
          />
          <p className="sgc-2fa__help">{t('otp.secretHelp')}</p>
          <PillButton type="submit">{t('otp.addEntry')}</PillButton>
        </form>
      ) : (
        <div className="sgc-2fa__add-form">
          <button type="button" className="sgc-2fa__upload-zone" onClick={() => fileInputRef.current?.click()}>
            <IconCircle icon={QrIcon} tone="accent" size={48} />
            <span>{t('otp.uploadQRImage')}</span>
          </button>
          {error && <p className="sgc-input__error">{error}</p>}
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFile} />
        </div>
      )}
    </Modal>
  );
}

function TwoFactor() {
  const { t } = useTranslation();
  const { entries, loading, codes, timeLeft, addEntry, deleteEntry } = useOtpEntries();
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState(null);
  const amber = timeLeft <= AMBER_THRESHOLD;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => (e.issuer || '').toLowerCase().includes(q) || (e.account || '').toLowerCase().includes(q));
  }, [entries, search]);

  const copy = useCallback((entry) => {
    const code = codes[entry.id];
    if (!code) return;
    navigator.clipboard.writeText(code);
    setToast({ type: 'success', message: t('otp.copy') });
  }, [codes, t]);

  const remove = useCallback((entry) => {
    if (!window.confirm(t('otp.confirmDelete'))) return;
    deleteEntry(entry.id);
  }, [deleteEntry, t]);

  return (
    <div className="sgc-2fa">
      <div className="sgc-2fa__header">
        <h1 className="sgc-2fa__title">{t('otp.title')}</h1>
        <PillButton icon={PlusIcon} size="sm" onClick={() => setShowAdd(true)}>{t('otp.addNew')}</PillButton>
      </div>

      <Input
        icon={SearchIcon}
        placeholder={t('otp.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <p className="sgc-2fa__loading">{t('common.loading')}</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ShieldIcon}
          title={entries.length === 0 ? t('otp.noEntries') : t('common.search')}
          description={entries.length === 0 ? t('otp.noEntriesDesc') : ''}
          actionLabel={entries.length === 0 ? t('otp.addNew') : undefined}
          onAction={entries.length === 0 ? () => setShowAdd(true) : undefined}
        />
      ) : (
        <ul className="sgc-2fa__list">
          {filtered.map((entry) => (
            <li key={entry.id} className="sgc-2fa__row" onClick={() => copy(entry)}>
              <span className="sgc-2fa__avatar">{initialsFor(entry)}</span>
              <div className="sgc-2fa__info">
                <p className="sgc-2fa__issuer">{entry.issuer || t('otp.unknownIssuer')}</p>
                <p className="sgc-2fa__account">{entry.account}</p>
              </div>
              <span className={`sgc-2fa__code ${amber ? 'sgc-2fa__code--amber' : ''}`}>
                {(codes[entry.id] || '------').replace(/(\d{3})(\d{3})/, '$1 $2')}
              </span>
              <CountdownRing timeLeft={timeLeft} amber={amber} />
              <button
                type="button"
                className="sgc-2fa__row-action"
                onClick={(e) => { e.stopPropagation(); copy(entry); }}
                title={t('otp.copy')}
              >
                <CopyIcon width={16} height={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="sgc-2fa__row-action"
                onClick={(e) => { e.stopPropagation(); remove(entry); }}
                title={t('otp.delete')}
              >
                <TrashIcon width={16} height={16} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <AddEntryModal open={showAdd} onClose={() => setShowAdd(false)} onAdd={addEntry} />

      {toast && <div className="sgc-2fa__toast"><Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} /></div>}
    </div>
  );
}

export default TwoFactor;
